import { useCallback, useEffect, useState } from 'react';
import { meetingAgentAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CalendarDays, CheckCircle2, Loader2, RefreshCw, Link2 } from 'lucide-react';

/** Admin / superadmin only — clients never see Meeting Agent Google connect. */
export default function MeetingAgentGoogleCard() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const res = await meetingAgentAPI.googleStatus();
      setStatus(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Could not load Google Calendar status.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    load();
  }, [isAdmin, load]);

  useEffect(() => {
    if (!isAdmin) return;
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('google_calendar');
    if (!flag) return;

    if (flag === 'connected') {
      toast.success('Google Calendar connected. This account is now the Meeting Agent inbox.');
      setActiveConnectionsHash();
      load();
    } else if (flag === 'error') {
      toast.error('Google Calendar connection failed. Try again.');
    }

    params.delete('google_calendar');
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash || '#connections'}`;
    window.history.replaceState({}, '', next);
  }, [isAdmin, load]);

  if (!isAdmin) {
    return null;
  }

  const connect = async () => {
    setBusy('connect');
    try {
      const res = await meetingAgentAPI.googleConnect();
      const url = res.data?.redirect_url;
      if (!url) throw new Error('Missing Google redirect URL');
      window.location.href = url;
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not start Google OAuth.');
      setBusy('');
    }
  };

  const syncNow = async () => {
    setBusy('sync');
    try {
      await meetingAgentAPI.googleSync();
      toast.success('Calendar sync queued.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sync failed.');
    } finally {
      setBusy('');
    }
  };

  const registerWatch = async () => {
    setBusy('watch');
    try {
      await meetingAgentAPI.googleWatch();
      toast.success('Calendar webhook watch registered.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Watch registration failed (needs public HTTPS webhook).');
    } finally {
      setBusy('');
    }
  };

  const connected = Boolean(status?.connected);
  const email = status?.connection?.google_email || status?.agent_email || null;

  return (
    <Card className="bg-card border-border/80" data-testid="meeting-agent-google-card">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <CalendarDays className="w-4 h-4" />
          Meeting Agent — Google Calendar
        </CardTitle>
        <CardDescription className="text-xs">
          Admin only. Connect one Google account as the Meeting Agent host. That account creates instant
          Google Meet links and receives calendar invites. After scope updates, use Reconnect so Meet
          create permission is granted.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading connection…
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">Status</span>
                {connected ? (
                  <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/15">
                    Connected
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    Not connected
                  </Badge>
                )}
              </div>
              {connected && email && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  AI agent email: <span className="text-foreground font-medium">{email}</span>
                </p>
              )}
              {connected && status?.connection?.last_synced_at && (
                <p className="text-xs text-muted-foreground">
                  Last synced: {new Date(status.connection.last_synced_at).toLocaleString()}
                </p>
              )}
              {connected && status?.connection?.watch?.expires_at && (
                <p className="text-xs text-muted-foreground">
                  Watch channel expires: {new Date(status.connection.watch.expires_at).toLocaleString()}
                </p>
              )}
              {!connected && (
                <p className="text-xs text-muted-foreground">
                  After connect, tell clients to invite this Google account email when they schedule meetings.
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={connect}
                disabled={!!busy}
                className="grad-accent text-white border-0 hover:opacity-90"
              >
                {busy === 'connect' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Link2 className="w-4 h-4 mr-2" />
                )}
                {connected ? 'Reconnect Google' : 'Connect Google Calendar'}
              </Button>
              {connected && (
                <>
                  <Button type="button" variant="outline" size="default" onClick={syncNow} disabled={!!busy}>
                    {busy === 'sync' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Sync now
                  </Button>
                  <Button type="button" variant="outline" size="default" onClick={registerWatch} disabled={!!busy}>
                    {busy === 'watch' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Register webhook watch
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function setActiveConnectionsHash() {
  if (window.location.hash !== '#connections') {
    window.location.hash = 'connections';
  }
}
